import React, {forwardRef, useImperativeHandle} from "react";
import { observer } from "mobx-react-lite";
import Box from "@mui/material/Box";
import {Crop169, CropPortrait, CropSquare} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import { Card, CardContent, ClickAwayListener } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import {
    ClipSize16And9RatioId,
    ClipSize1And1RatioId,
    ClipSize9And16RatioId,
    DisplayToClipSizeId
} from "../../utils/utils";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import { canvasSizeForPresetAndTier, getCanvasTierFromCanvasSize } from "../../utils/canvasSizing";

/**
 * Props:
 *  - visible: boolean
 *  - handleClose: () => void
 *  - setAspectRatioId?: (id) => void
 *  - aspectRatioId?: current id
 *  - topPosition?: number  (px)  // relative to the positioned parent (canvas region)
 *  - leftPosition?: number (px)
 */
const AspectRatioSelectCard = observer(forwardRef(({
                                                       visible,
                                                       containerRef,
                                                       canvasRef,
                                                       handleClose,
                                                       topPosition = 56,
                                                       leftPosition = 12,
                                                       aspectRatioId: aspectRatioIdProp,
                                                       setAspectRatioId: setAspectRatioIdProp,
                                                   }, ref) => {

    // Derive current aspect from props if provided, else from store (keeps in sync via observer)
    const currentAspectId = aspectRatioIdProp ?? DisplayToClipSizeId({ width: CompositionStateStore.display.width, height: CompositionStateStore.display.height });

    const handleClipSizeChange = (clipSizeId) => {
        const comp = CompositionService.composition;
        if (!comp) return;

        // Preserve the current resolution tier (short-side), only switching aspect.
        const currentW = Number(comp?.settings?.width ?? CompositionStateStore.display.width ?? 1920);
        const currentH = Number(comp?.settings?.height ?? CompositionStateStore.display.height ?? 1080);
        const tier = getCanvasTierFromCanvasSize(currentW, currentH);

        const preset =
            clipSizeId === ClipSize9And16RatioId
                ? "vertical"
                : clipSizeId === ClipSize1And1RatioId
                    ? "square"
                    : "horizontal";
        const { width, height } = canvasSizeForPresetAndTier(preset, tier);
        comp.resize(width, height);
        setAspectRatioIdProp?.(clipSizeId);
        handleClose();
    };

    const featureDescStyle = {
        alignSelf: "stretch",
        color: "#999",
        fontFamily: "Inter",
        fontSize: "10px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "12px",
        letterSpacing: "0.48px",
    };

    const ClipSizeSelectBox = ({ inputClipSize, ratioText, clipSizeIcon, surfacesText, disabled, dataTour }) => {
        return (
            <Box
                data-tour={dataTour}
                onClick={() => {
                    if (disabled) return;
                    handleClipSizeChange(inputClipSize)
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingX: 0.75,
                    marginBottom: 0.5,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.3s, border 0.3s',
                    border: '1px solid transparent',
                    borderRadius: '8px',
                    '&:hover': {
                        border: '1px solid',
                        borderColor: disabled ? 'transparent' : '#666666',
                        borderRadius: '8px',
                    },
                }}
            >
                <Box flexDirection="row" display="flex" justifyContent="flex-start" gap={1}>
                    {clipSizeIcon}
                    <Box flexDirection="column">
                        <Typography variant="subtitle2" color="text.primary" fontSize="0.9rem" fontWeight={400}>
                            {ratioText}
                        </Typography>
                        <Box sx={featureDescStyle}>{surfacesText}</Box>
                    </Box>
                </Box>
                {currentAspectId === inputClipSize && <CheckIcon sx={{fontSize: '1.2rem', color: 'green'}}/>}
            </Box>
        )
    };

    useImperativeHandle(ref, () => ({ handleClipSizeChange }));

    if (!visible) return null;

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Card
                elevation={5}
                style={{
                    cursor: 'pointer',
                    position: 'absolute',
                    top: `${topPosition}px`,
                    left: `${leftPosition}px`,
                    width: 230,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '10px',
                    overflow: 'visible',
                    height: 155,
                    zIndex: 9999
                }}
            >
                <CardContent>
                    <ClipSizeSelectBox
                        inputClipSize={ClipSize16And9RatioId}
                        clipSizeIcon={<Crop169 sx={{color: "#999999"}}/>}
                        ratioText="16:9"
                        surfacesText="YouTube, Spotify"
                        dataTour="clip-size-option-16-9"
                    />
                    <ClipSizeSelectBox
                        inputClipSize={ClipSize9And16RatioId}
                        clipSizeIcon={<CropPortrait sx={{color: "#999999"}}/>}
                        ratioText="9:16"
                        surfacesText="Shorts, Reels, TikToks"
                        disabled={false}
                        dataTour="clip-size-option-9-16"
                    />
                    <ClipSizeSelectBox
                        inputClipSize={ClipSize1And1RatioId}
                        clipSizeIcon={<CropSquare sx={{color: "#999999"}}/>}
                        ratioText="1:1"
                        surfacesText="LinkedIn, X, Instagram"
                        dataTour="clip-size-option-1-1"
                    />
                </CardContent>
            </Card>
        </ClickAwayListener>
    )
}));

export default AspectRatioSelectCard;
